'use client'

import { useState, useRef, useEffect } from 'react'
import { useUser } from '@/contexts/user/UserContext'
import { useRouter, usePathname } from 'next/navigation'
import Image from 'next/image'
import Link from 'next/link'
import { Calendar, User, LogOut, ChevronDown, Shield, Eye, X, Users, Bug, Lock, FileText, FileSignature, Wrench, Megaphone, LifeBuoy, Heart, Briefcase, CircleDollarSign } from 'lucide-react'
import { MessagesUnreadBadge } from './MessagesUnreadBadge'
import { logger } from '@/lib/services/logger.service'

// Content reviewers - can access /admin/resources only
const CONTENT_REVIEWER_IDS = ['e9db8020-529b-48f4-887a-a26dada65aa2']

export function Header() {
  const { profile, impersonatedProfile, stopImpersonation, logout } = useUser()
  const [isSigningOut, setIsSigningOut] = useState(false)
  const router = useRouter()
  const pathname = usePathname()
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Debug log
  useEffect(() => {
    logger.debug('[Header] Profile state:', { profile })
  }, [profile])

  const handleLogoClick = () => {
    router.push('/profile-builder')
  }

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Build navigation based on user role
  const navigation: { name: string; href: string; current: boolean }[] = [
    {
      name: 'Schedule',
      href: '/schedule',
      current: pathname === '/schedule'
    },
    {
      name: 'Clients',
      href: '/clients',
      current: pathname === '/clients'
    },
    {
      name: 'Dashboard',
      href: '/dashboard',
      current: pathname === '/dashboard'
    },
    {
      name: 'Tools',
      href: '/tools',
      current: pathname === '/tools' || pathname?.startsWith('/tools/')
    },
    {
      name: 'Support',
      href: '/support/tickets',
      current: pathname?.startsWith('/support') || false
    },
  ]

  // Single "Supervision Hub" link replaces the earlier trio (Supervision
  // Schedule / Supervision / Between Sessions). Every supervision surface
  // lives under `/supervision-hub/*` — schedule, requests, care, logs,
  // reflections, attestations, agreements, directory. The BS product
  // management dashboard at `/supervision/between-sessions` is still
  // reachable by URL for the roster/wallet/subscriber flows, but isn't
  // headline nav anymore.
  if (profile?.isSupervisor) {
    navigation.push({
      name: 'Supervision Hub',
      href: '/supervision-hub',
      current: pathname?.startsWith('/supervision-hub') || false
    })
  }

  // Add Group Supervision link for FTAs — employee therapists who
  // aren't part of Between Sessions and aren't supervisors. They book
  // 1 group / month with no credit system. Hidden for supervisors
  // (they have their own Supervision link) and BS members (handled
  // through the BS app).
  const isFta =
    profile?.employmentStatus === 'EMPLOYEE'
    && profile?.isBetweenSession !== true
    && profile?.isSupervisor !== true
  if (isFta) {
    navigation.push({
      name: 'Group Supervision',
      href: '/supervision/groups',
      current: pathname?.startsWith('/supervision/groups') || false,
    })
  }

  // Book Supervision link for interns — RESIDENTs who have a matched
  // supervisor. BS membership is orthogonal (a resident can also be a
  // BS member and still use this flow for their intern-side dyad
  // series). Page gates further on having a matched supervisor set.
  // Server-side eligibility endpoint enforces the same rules, so a
  // mismatched click just lands on the empty "not available yet" state.
  const isIntern =
    profile?.employmentStatus === 'RESIDENT'
    && profile?.isSupervisor !== true
  if (isIntern) {
    navigation.push({
      name: 'Book Supervision',
      href: '/supervision/book',
      current: pathname?.startsWith('/supervision/book') || false,
    })
  }
  // Note: /agreements is intentionally NOT surfaced in top nav — it's a
  // one-time signing surface. Discoverability lives on the /schedule
  // banner (PendingAgreementsBanner) which links straight to /agreements
  // whenever a signature is outstanding, plus an "Agreements" link
  // under the avatar dropdown (below) for interns who want to review
  // signed copies later.

  // Job board eligibility — residents (primary audience), admins (for
  // direct browsing / impersonation support), and BS members (so a BS
  // clinician who happens to sign into the therapist app doesn't lose
  // the board). Supervisors intentionally excluded. When an admin
  // impersonates, `profile` here is already the impersonated user's
  // (UserContext returns impersonatedProfile ?? actualProfile), so this
  // check naturally shows the link if the impersonated user qualifies.
  //
  // Lives in the avatar dropdown, not the top nav — the board is a
  // secondary surface and the top nav is reserved for daily-use tabs
  // (Schedule / Clients / Dashboard / Tools / Support).
  const canAccessJobBoard =
    profile?.employmentStatus === 'RESIDENT'
    || profile?.isAdmin === true
    || profile?.isBetweenSession === true

  // Get first character of email for avatar
  const getInitials = (email: string) => {
    const trimmed = email?.trim()
    if (!trimmed || trimmed.length === 0) return 'U'
    return trimmed[0]!.toUpperCase()
  }

  return (
    <header className="bg-white border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo and Navigation */}
          <div className="flex items-center space-x-8">
            <button onClick={handleLogoClick} className="flex items-center">
              <Image
                src="/alli_logo.png"
                alt="Alli Logo"
                width={120}
                height={40}
                className="h-8 w-auto"
              />
            </button>

            {/* Navigation Links */}
            <nav className="hidden md:flex space-x-8">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`
                    inline-flex items-center text-sm font-medium transition-opacity
                    ${item.current
                      ? 'text-gray-900'
                      : 'text-gray-400 hover:text-gray-600'
                    }
                  `}
                >
                  {item.name}
                </Link>
              ))}
              {/* Messages link — surfaces iCBT client ↔ therapist
                *  threads with a live unread-count badge. Kept out
                *  of the navigation array because it uniquely renders
                *  the <MessagesUnreadBadge> inline. */}
              <Link
                href="/messages"
                className={`
                  inline-flex items-center text-sm font-medium transition-opacity
                  ${pathname?.startsWith('/messages')
                    ? 'text-gray-900'
                    : 'text-gray-400 hover:text-gray-600'
                  }
                `}
              >
                Messages
                <MessagesUnreadBadge />
              </Link>
            </nav>
          </div>

          {/* Impersonation Indicator */}
          {impersonatedProfile && (
            <div className="flex items-center mr-4">
              <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-2">
                <Eye className="w-3 h-3" />
                <span>Viewing as {impersonatedProfile.firstName || impersonatedProfile.email}</span>
                <button
                  onClick={stopImpersonation}
                  className="ml-2 hover:text-amber-900"
                  title="Exit impersonation"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          )}

          {/* User Menu */}
          {profile && (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                <div className="h-8 w-8 rounded-full bg-primary-500 flex items-center justify-center text-white font-medium">
                  {getInitials(profile.email || '')}
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-500 transition-transform ${dropdownOpen ? 'rotate-180' : ''}`} />
              </button>

              {/* Dropdown Menu */}
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50">
                  <div className="py-1">
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-200">
                      <p className="font-medium">{profile?.firstName ? `${profile.firstName}` : profile?.email}</p>
                      {profile?.firstName && <p className="text-xs text-gray-500">{profile?.email}</p>}
                    </div>
                    
                    <Link
                      href="/profile-builder"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <User className="mr-3 h-4 w-4 text-gray-400" />
                      My Profile
                    </Link>

                    <Link
                      href="/settings/security"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Lock className="mr-3 h-4 w-4 text-gray-400" />
                      Security
                    </Link>

                    <Link
                      href="/development"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Bug className="mr-3 h-4 w-4 text-gray-400" />
                      Development Tracker
                    </Link>

                    <Link
                      href="/newsfeed"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Megaphone className="mr-3 h-4 w-4 text-gray-400" />
                      Newsfeed
                    </Link>

                    {/* Agreements — one-time signing surface for interns.
                        Kept under the dropdown (not top nav) because it's
                        not day-to-day. Supervisors use /supervision-hub/agreements
                        via their main-nav Supervision Hub link. */}
                    {isIntern && (
                      <Link
                        href="/agreements"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <FileSignature className="mr-3 h-4 w-4 text-gray-400" />
                        Agreements
                      </Link>
                    )}

                    {canAccessJobBoard && (
                      <Link
                        href="/jobs"
                        className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <Briefcase className="mr-3 h-4 w-4 text-gray-400" />
                        Job Board
                      </Link>
                    )}

                    <Link
                      href="/dashboard/care"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Heart className="mr-3 h-4 w-4 text-gray-400" />
                      CARE Program
                    </Link>

                    {/* Content Reviewer Link - only show for content reviewers */}
                    {profile?.id && CONTENT_REVIEWER_IDS.includes(profile.id) && (
                      <div className="border-t border-gray-200 mt-2 pt-2">
                        <Link
                          href="/admin/resources"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <FileText className="mr-3 h-4 w-4 text-gray-400" />
                          Review Resources
                        </Link>
                      </div>
                    )}

                    {profile?.employmentStatus === 'CONTRACTOR' && (
                      <div className="border-t border-gray-200 mt-2 pt-2">
                        <Link
                          href={
                            impersonatedProfile
                              ? `/payout-setup?proId=${impersonatedProfile.id}`
                              : '/payout-setup'
                          }
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <CircleDollarSign className="mr-3 h-4 w-4 text-gray-400" />
                          Payouts &amp; invoices
                        </Link>
                      </div>
                    )}

                    {/* Events — full-time employees can host events
                        (backend allows via canHostEvents). Lands on the
                        list page where existing events are visible and a
                        "Create Event" button is one click away. Admins see
                        this in addition to their Admin Dashboard entry. */}
                    {profile?.employmentStatus === 'EMPLOYEE' && (
                      <div className="border-t border-gray-200 mt-2 pt-2">
                        <Link
                          href="/admin/events"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <Calendar className="mr-3 h-4 w-4 text-gray-400" />
                          Events
                        </Link>
                      </div>
                    )}

                    {/* Admin Links - only show for admin users */}
                    {profile?.isAdmin && (
                      <div className="border-t border-gray-200 mt-2 pt-2">
                        <Link
                          href="/admin"
                          className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 font-medium"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <Shield className="mr-3 h-4 w-4 text-gray-400" />
                          Admin Dashboard
                        </Link>
                      </div>
                    )}
                    
                    <Link
                      href="/schedule"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 md:hidden"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Calendar className="mr-3 h-4 w-4 text-gray-400" />
                      Schedule
                    </Link>
                    
                    <Link
                      href="/clients"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 md:hidden"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <Users className="mr-3 h-4 w-4 text-gray-400" />
                      Clients
                    </Link>

                    <Link
                      href="/support/tickets"
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 md:hidden"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <LifeBuoy className="mr-3 h-4 w-4 text-gray-400" />
                      Support
                    </Link>

                    <button
                      onClick={async () => {
                        console.log('[Header] Sign out button clicked')
                        logger.debug('[Header] Sign out button clicked')
                        setDropdownOpen(false)
                        setIsSigningOut(true)
                        try {
                          console.log('[Header] About to call logout function')
                          logger.debug('[Header] Calling logout...')
                          await logout()
                          console.log('[Header] logout completed')
                          logger.debug('[Header] logout completed')
                        } catch (error) {
                          console.error('[Header] Error during logout:', error)
                          logger.error('Failed to logout from header', error, {
                            component: 'Header',
                            action: 'logout',
                            errorType: (error as any)?.code || 'unknown',
                            errorMessage: (error as any)?.message
                          })
                          setIsSigningOut(false)
                        }
                      }}
                      disabled={isSigningOut}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                    >
                      <LogOut className="mr-3 h-4 w-4 text-gray-400" />
                      {isSigningOut ? 'Signing out...' : 'Sign Out'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </header>
  )
}